export class ApiError extends Error {
    constructor(message, status = 0, code = 'API_ERROR', details = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

const clearAdminAccess = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('key');
    window.dispatchEvent(new CustomEvent('admin-auth-changed', {
        detail: { isAdmin: false }
    }));
};

export const logoutAdmin = () => {
    clearAdminAccess();
};

const getErrorMessage = (payload, fallback) => {
    if (typeof payload?.error === 'string') return payload.error;
    return payload?.error?.message || payload?.message || fallback;
};

const apiRequest = async (path, { body = {}, admin = false, signal } = {}) => {
    const isFormData = body instanceof FormData;
    const headers = { Accept: 'application/json' };

    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }

    if (admin) {
        const key = localStorage.getItem('key')?.trim() || '';
        if (!key) {
            clearAdminAccess();
            throw new ApiError('Klucz administracyjny jest wymagany.', 401, 'ADMIN_KEY_REQUIRED');
        }
        headers['X-Admin-Key'] = key;
    }

    let response;
    try {
        response = await fetch(`/api/${path}`, {
            method: 'POST',
            headers,
            body: isFormData ? body : JSON.stringify(body),
            signal,
        });
    } catch (error) {
        if (error?.name === 'AbortError') throw error;
        throw new ApiError('Brak połączenia z serwerem. Spróbuj ponownie.', 0, 'NETWORK_ERROR');
    }

    const raw = await response.text();
    let payload = {};
    if (raw) {
        try {
            payload = JSON.parse(raw);
        } catch {
            throw new ApiError('Serwer zwrócił nieprawidłową odpowiedź.', response.status, 'INVALID_RESPONSE');
        }
    }

    if (admin && (response.status === 401 || response.status === 403)) {
        clearAdminAccess();
    }

    if (!response.ok || payload?.success === false) {
        throw new ApiError(
            getErrorMessage(payload, `Błąd serwera (${response.status}).`),
            response.status,
            payload?.error?.code || 'API_ERROR',
            payload?.error?.details || {}
        );
    }

    return payload;
};

export const getApiErrorMessage = (error, fallback = 'Wystąpił nieoczekiwany błąd.') => {
    if (error instanceof ApiError || error instanceof Error) {
        return error.message || fallback;
    }
    return fallback;
};

export const getEvents = async (limit = null, offset = null) => {
    const body = {};
    if (limit !== null) body.limit = limit;
    if (offset !== null) body.offset = offset;
    return apiRequest('get-events', { body });
};

export const getContacts = async () => {
    const result = await apiRequest('get-contacts');
    return result.data || [];
};

export const getEventByID = async (id) => {
    const result = await apiRequest('get-event-by-id', { body: { id } });
    return result.data;
};

export const updateEventByID = (formData) => (
    apiRequest('update-event', { body: formData, admin: true })
);

export const updateGallery = (formData) => (
    apiRequest('update-gallery', { body: formData, admin: true })
);

export const loadFullGallery = async (limit = null, offset = null) => {
    const body = {};
    if (limit !== null) body.limit = limit;
    if (offset !== null) body.offset = offset;
    return apiRequest('get-gallery', { body });
};

export const loadGalleryByID = (id) => (
    apiRequest('get-gallery-by-id', { body: { id } })
);

export const createEvent = async (formData) => {
    const result = await apiRequest('create-event', { body: formData, admin: true });
    return result.data;
};

export const deleteEventByID = (id) => (
    apiRequest('delete-event', { body: { id }, admin: true })
);

export const updateContact = (data) => {
    const { key, ...safeData } = data || {};
    return apiRequest('update-contact', { body: safeData, admin: true });
};

export const sendAdminKey = async (key) => {
    const normalizedKey = key?.trim() || '';
    const result = await apiRequest('admin/verify', { body: { key: normalizedKey } });

    if (result?.data?.authorized === true) {
        localStorage.setItem('isAdmin', 'true');
        localStorage.setItem('key', normalizedKey);
        window.dispatchEvent(new CustomEvent('admin-auth-changed', {
            detail: { isAdmin: true }
        }));
    }

    return {
        result: result?.data?.authorized === true,
        message: result?.message,
    };
};

export const checkIsAdmin = async () => {
    if (!localStorage.getItem('key')?.trim()) {
        clearAdminAccess();
        return false;
    }

    try {
        const result = await apiRequest('admin/is-admin', { admin: true });
        return result?.data?.authorized === true;
    } catch {
        return false;
    }
};
